import React, { useState } from "react";

import { RecommendationModule } from "packages/ui-shared/components/RecommendationModule/RecommendationModule";
import type {
  ProductCardProduct,
  RecommendationSection,
  VariantType
} from "@shared/graphql.types";
import { useSliderWithFiveItems } from "packages/ui-shared/components/CarouselModule/hooks/useCarouselProps";

import { ATBConfirmation } from "basket_and_checkout/shared/components/ATBConfirmation";
import { CarouselLoader } from "packages/ui-shared/components/CarouselLoader/CarouselLoader";
import * as styles from "./Recommendations.css";
import type { GetRecommendationType } from "packages/ui-shared/components/RecommendationModule/hooks/useRecommendations";
import { useRecommendations } from "packages/ui-shared/components/RecommendationModule/hooks/useRecommendations";

const queryArguments = (
  isOutOfStock: boolean,
  sku: string
): GetRecommendationType =>
  isOutOfStock
    ? {
        locationType: "PDP_OOS",
        config: {
          alternativeToSku: sku,
          recommender: "constructor:pdp-out-of-stock-alternatives"
        }
      }
    : {
        locationType: "PDP_IN_STOCK",
        config: {
          complementaryToSku: sku,
          recommender: "constructor:pdp-complimentary"
        }
      };

export const Recommendations: React.FC<{
  isOutOfStock: boolean;
  sku: string;
}> = ({ isOutOfStock, sku }) => {
  const queryParams = [queryArguments(isOutOfStock, sku)];

  const { data: { getRecommendations: recommendations } = {}, loading } =
    useRecommendations(queryParams);

  const [confirmation, setConfirmation] = useState(false);
  const [confirmationProduct, setConfirmationProduct] = useState<
    ProductCardProduct | VariantType
  >();

  const openConfirmationModal = (product: ProductCardProduct | VariantType) => {
    setConfirmationProduct(product);
    setConfirmation(true);
  };

  if (loading) return <CarouselLoader />;

  if (!recommendations || recommendations.items.length === 0) {
    return <></>;
  }

  const hasVisibleRecommendations = recommendations.items.every(
    ({ variants }) => variants.length > 0
  );

  if (!hasVisibleRecommendations) {
    return <></>;
  }

  return (
    <div
      id="frequently-bought-together"
      data-testid="frequently-bought-together"
      className={styles.wrapper}
    >
      {confirmationProduct && (
        <ATBConfirmation
          confirmation={confirmation}
          setConfirmation={setConfirmation}
          variant={confirmationProduct}
          source="PDP"
        />
      )}
      {recommendations.items.map((recs) => (
        <RecommendationsWrapper
          key={recs.id}
          onAddToBag={openConfirmationModal}
          rec={recs}
        />
      ))}
    </div>
  );
};

const RecommendationsWrapper: React.FC<{
  rec: RecommendationSection;
  onAddToBag: (product: ProductCardProduct | VariantType) => void;
}> = ({ rec, onAddToBag }) => {
  const sliderWithFourItemsProps = useSliderWithFiveItems({
    itemsLength: rec.variants.length
  });

  return (
    <RecommendationModule
      key={rec.id}
      {...rec}
      onAddToBag={onAddToBag}
      carouselPropsOverride={{
        ...sliderWithFourItemsProps,
        scrollbar: true,
        showNavigation: false
      }}
    />
  );
};
